import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingCart, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';

export default function CartPage() {
  const { user, loading: authLoading } = useAuth();
  const { cart, fetchCart, updateItem, removeItem, clearCart, loading } = useCart();
  const navigate = useNavigate();
  const [orderLoading, setOrderLoading] = useState(false);

  useEffect(() => { if (user) fetchCart(); }, [user, fetchCart]);

  if (authLoading) return <div className="max-w-3xl mx-auto px-4 py-12"><div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-gray-200 rounded" />)}</div></div>;

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center" data-testid="cart-page-auth">
        <ShoppingCart size={48} className="mx-auto text-gray-300 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "'Chivo', sans-serif" }}>Sign in to view your cart</h2>
        <p className="text-gray-500 mb-6">Please log in to add items and view your shopping cart.</p>
        <Link to="/login" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-3 px-8 rounded inline-block" data-testid="cart-login-btn">Sign In</Link>
      </div>
    );
  }

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-12"><div className="animate-pulse space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 bg-gray-200 rounded" />)}</div></div>;

  const items = cart.items || [];

  return (
    <div className="max-w-5xl mx-auto px-4 py-8" data-testid="cart-page">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight mb-6" style={{ fontFamily: "'Chivo', sans-serif" }}>
        Shopping Cart {items.length > 0 && <span className="text-gray-400 text-lg font-normal">({items.length} item{items.length !== 1 ? 's' : ''})</span>}
      </h1>

      {items.length === 0 ? (
        <div className="text-center py-16 bg-white border rounded" data-testid="empty-cart">
          <ShoppingCart size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-bold text-gray-900 mb-2">Your cart is empty</h3>
          <p className="text-gray-500 text-sm mb-6">Start shopping to fill your cart with quality auto parts.</p>
          <Link to="/products" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-3 px-8 rounded inline-block" data-testid="cart-browse-btn">
            Browse Products
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Items */}
          <div className="lg:col-span-2 space-y-3">
            {items.map(item => (
              <div key={item.product_id} className="bg-white border rounded p-4 flex gap-4" data-testid={`cart-item-${item.product_id}`}>
                <Link to={`/products/${item.product_id}`} className="shrink-0">
                  <img src={item.product?.image_url || 'https://placehold.co/100x100'} alt={item.product?.name} className="w-24 h-24 object-cover rounded" />
                </Link>
                <div className="flex-1 min-w-0">
                  <Link to={`/products/${item.product_id}`} className="text-sm font-bold text-gray-900 hover:text-[#0F766E] line-clamp-2" style={{ fontFamily: "'Chivo', sans-serif" }}>
                    {item.product?.name}
                  </Link>
                  <p className="text-xs text-gray-500 mt-1">{item.product?.brand}</p>
                  <p className="text-lg font-black text-[#0F766E] mt-2">${item.item_total?.toFixed(2)}</p>
                </div>
                <div className="flex flex-col items-end justify-between">
                  <button onClick={() => removeItem(item.product_id)} className="text-gray-400 hover:text-red-500 transition-colors" data-testid={`remove-item-${item.product_id}`}>
                    <Trash2 size={18} />
                  </button>
                  <div className="flex items-center border rounded">
                    <button onClick={() => updateItem(item.product_id, item.quantity - 1)} className="px-2 py-1 hover:bg-gray-100" data-testid={`decrease-${item.product_id}`}><Minus size={14} /></button>
                    <span className="w-8 text-center text-sm font-semibold" data-testid={`qty-${item.product_id}`}>{item.quantity}</span>
                    <button onClick={() => updateItem(item.product_id, item.quantity + 1)} className="px-2 py-1 hover:bg-gray-100" data-testid={`increase-${item.product_id}`}><Plus size={14} /></button>
                  </div>
                </div>
              </div>
            ))}
            <button onClick={clearCart} className="text-sm text-red-600 font-medium hover:underline" data-testid="clear-cart-btn">Clear Cart</button>
          </div>

          {/* Summary */}
          <div className="bg-white border rounded p-6 h-fit sticky top-24" data-testid="cart-summary">
            <h3 className="text-lg font-bold text-gray-900 mb-4" style={{ fontFamily: "'Chivo', sans-serif" }}>Order Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span className="font-semibold">${cart.total?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Shipping</span>
                <span className="font-semibold text-green-600">{cart.total >= 50 ? 'Free' : '$9.99'}</span>
              </div>
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="font-bold text-gray-900">Total</span>
                  <span className="font-black text-xl text-[#0F766E]" data-testid="cart-total">
                    ${(cart.total + (cart.total >= 50 ? 0 : 9.99)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <Link
              to="/checkout"
              className="w-full mt-6 bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-3 rounded transition-all active:scale-95 flex items-center justify-center gap-2 block text-center"
              data-testid="checkout-btn"
            >
              Proceed to Checkout <ArrowRight size={18} />
            </Link>
            {cart.total < 50 && (
              <p className="text-xs text-gray-500 text-center mt-3">Add ${(50 - cart.total).toFixed(2)} more for free shipping!</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
